import { createSlice } from '@reduxjs/toolkit';
import { fetchActivityLogsForUserProjectDate } from './thunks/fetchActivityLogsForUserProjectDate.thunk';
import {
  fetchProjectActivityFeed,
  isShownInProjectFeed,
} from './thunks/fetchProjectActivityFeed.thunk';

const emptyDialog = {
  open: false,
  userId: null,
  projectId: null,
  date: null,
};

// The project dashboard's activity feed (newest first) of one project at a
// time; `nextBefore` is the cursor of the next older page, null at the end
// (and it can be set while `items` is empty: the thunk follows the cursor past
// pages with nothing to show only so far, see fetchProjectActivityFeed).
const emptyProjectFeed = {
  projectId: null,
  items: [],
  nextBefore: null,
  loading: false,
  loadingMore: false,
  error: null,
};

const initialState = {
  dialog: emptyDialog,
  items: [],
  loading: false,
  error: null,
  projectFeed: emptyProjectFeed,
};

const activityLogSlice = createSlice({
  name: 'activityLog',
  initialState,
  reducers: {
    openActivityLogDialog: (state, action) => {
      const { userId, projectId, date } = action.payload || {};
      state.dialog = { open: true, userId, projectId, date };
      state.items = [];
      state.error = null;
    },
    closeActivityLogDialog: (state) => {
      state.dialog = emptyDialog;
      state.items = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivityLogsForUserProjectDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchActivityLogsForUserProjectDate.fulfilled,
        (state, action) => {
          state.loading = false;
          state.items = action.payload || [];
        }
      )
      .addCase(
        fetchActivityLogsForUserProjectDate.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.error?.message || 'Failed to load activity logs';
        }
      )
      // A first page starts the feed over for its project (keeping what is
      // shown while the same project reloads); an older page only marks
      // itself loading. Answers for a project the feed has left are dropped.
      .addCase(fetchProjectActivityFeed.pending, (state, action) => {
        const { projectId, before } = action.meta.arg;
        const feed = state.projectFeed;
        if (before) {
          if (feed.projectId === projectId) feed.loadingMore = true;
          return;
        }
        state.projectFeed = {
          ...emptyProjectFeed,
          projectId,
          items: feed.projectId === projectId ? feed.items : [],
          nextBefore: feed.projectId === projectId ? feed.nextBefore : null,
          loading: true,
        };
      })
      .addCase(fetchProjectActivityFeed.fulfilled, (state, action) => {
        const { projectId, before } = action.meta.arg;
        const feed = state.projectFeed;
        if (feed.projectId !== projectId) return;
        // The cursor is the API's, whatever of the page is left out here: a
        // page can leave nothing to show while older rows follow.
        const { nextBefore } = action.payload;
        const items = action.payload.items.filter(isShownInProjectFeed);
        if (before) {
          const known = new Set(feed.items.map((item) => item.sk));
          feed.items = feed.items.concat(
            items.filter((item) => !known.has(item.sk))
          );
          feed.loadingMore = false;
        } else {
          feed.items = items;
          feed.loading = false;
        }
        feed.nextBefore = nextBefore;
        feed.error = null;
      })
      .addCase(fetchProjectActivityFeed.rejected, (state, action) => {
        const { projectId, before } = action.meta.arg;
        const feed = state.projectFeed;
        if (feed.projectId !== projectId) return;
        if (before) {
          feed.loadingMore = false;
        } else {
          feed.loading = false;
        }
        feed.error = action.error?.message || 'Failed to load activity logs';
      });
  },
});

export const { openActivityLogDialog, closeActivityLogDialog } =
  activityLogSlice.actions;

export const selectActivityLogDialog = (state) => state.activityLog.dialog;
export const selectActivityLogItems = (state) => state.activityLog.items;
export const selectActivityLogLoading = (state) => state.activityLog.loading;
export const selectProjectActivityFeed = (state) =>
  state.activityLog.projectFeed;

export default activityLogSlice.reducer;
